/**
 * Invariant Lifecycle Governance — pure domain types and transitions.
 *
 * Mirrors `Foundation/Governance/InvariantLifecycleRecords` (Agda) and the
 * INV.ESTABLISH / INV.PRESERVE theorems realized in `@csk/core`. A lifecycle
 * record tracks one invariant through five status values:
 *
 *   Proposed → Established → Preserved* → (Violated | Retired)
 *
 * Unconstructability — where it is structural, where it is runtime-enforced.
 *
 *   In the Agda module, the `LifecyclePayload` data type is *indexed* by
 *   `InvariantStatus`. The constructor `payload-violated` only inhabits
 *   `LifecyclePayload tracked Inv-Violated`, and only from an
 *   `ActiveLifecycle` argument (Established or Preserved). A `payload-violated`
 *   value cannot be constructed from a `Proposed` payload because no Agda
 *   term has the requisite type. Invalid transitions are therefore
 *   structurally inexpressible — there is no closed Agda term that names them.
 *
 *   TypeScript's structural type system cannot encode the same dependent
 *   indexing without significant ergonomic cost (a status-parameterized
 *   `InvariantLifecycleRecord<S>` plus per-status transition signatures).
 *   This realization therefore captures the same discipline at *runtime*:
 *   each transition function inspects the record's status and throws
 *   `LifecycleTransitionError` if the requested move is not permitted.
 *   Transition errors are total and carry both `from` and `attempted` so the
 *   rejection can be reported structurally to the UI.
 *
 *   The runtime guarantee in TypeScript is: no `InvariantLifecycleRecord`
 *   with a forbidden (status, latest-witness) combination can be produced
 *   by this module's exported API. The record type is **branded** with a
 *   private symbol, so external code cannot fabricate values of type
 *   `InvariantLifecycleRecord` by writing an object literal — every
 *   `InvariantLifecycleRecord` necessarily flowed through `propose` plus
 *   zero or more typed transitions, each of which preserves the typestate
 *   invariant.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

// ── Status enum (mirrors Agda InvariantStatus) ────────────────

/** Lifecycle status values for an invariant; canonical names from the Agda module. */
export type InvariantStatus = "Proposed" | "Established" | "Preserved" | "Violated" | "Retired";

/** All status values, in canonical narrative order. */
export const INVARIANT_STATUSES: readonly InvariantStatus[] = [
  "Proposed",
  "Established",
  "Preserved",
  "Violated",
  "Retired",
] as const;

/** Status values from which a Violation or Retirement may be recorded. */
export type ActiveStatus = "Established" | "Preserved";

/** Status values at which the lifecycle has terminated. */
export type TerminalStatus = "Violated" | "Retired";

/** Whether `s` is an active (non-terminal, post-establishment) status. */
export function isActiveStatus(s: InvariantStatus): s is ActiveStatus {
  return s === "Established" || s === "Preserved";
}

/** Whether `s` is a terminal status. */
export function isTerminalStatus(s: InvariantStatus): s is TerminalStatus {
  return s === "Violated" || s === "Retired";
}

// ── Witnesses (mirror Agda Establishment/Preservation/Violation/RetirementWitness) ──

/** Common fields shared by every lifecycle witness. */
export interface WitnessBase {
  /** ISO-8601 timestamp at which the witness was recorded. */
  readonly at: string;
  /** Free-form evidence reference (digest, artifact id, etc.). */
  readonly evidence: string;
}

export interface EstablishmentWitness extends WitnessBase {
  readonly kind: "establishment";
  readonly initialState: string;
}

export interface PreservationWitness extends WitnessBase {
  readonly kind: "preservation";
  readonly preState: string;
  readonly postState: string;
}

export interface ViolationWitness extends WitnessBase {
  readonly kind: "violation";
  readonly state: string;
  readonly reason: string;
}

export interface RetirementWitness extends WitnessBase {
  readonly kind: "retirement";
  readonly state: string;
}

export type LifecycleWitness =
  | EstablishmentWitness
  | PreservationWitness
  | ViolationWitness
  | RetirementWitness;

// ── Lifecycle record ──────────────────────────────────────────

/**
 * Internal shape of a lifecycle record. Not exported directly; consumers see
 * `InvariantLifecycleRecord`, which is this shape intersected with a unique
 * brand. The brand makes the record nominally typed: a value of type
 * `InvariantLifecycleRecord` cannot be constructed by external code without
 * going through `propose` + the four typed transitions, so the in-memory
 * invariant (status agrees with the latest witness) is a true structural
 * guarantee, not a wording softener.
 */
interface InvariantLifecycleRecordShape {
  readonly invariant: string;
  readonly description: string;
  readonly status: InvariantStatus;
  readonly history: readonly LifecycleWitness[];
}

declare const lifecycleRecordBrand: unique symbol;

/**
 * A complete lifecycle record for a single tracked invariant.
 *
 * Status agrees with the latest witness by construction. Append-only `history`
 * makes the timeline auditable. The unique brand prevents consumers from
 * forging records that bypass the runtime guards in `propose` / `establish` /
 * `preserve` / `violate` / `retire`.
 */
export type InvariantLifecycleRecord = InvariantLifecycleRecordShape & {
  readonly [lifecycleRecordBrand]: "InvariantLifecycleRecord";
};

/** Internal helper: tag a shape as a branded `InvariantLifecycleRecord`. */
function brand(shape: InvariantLifecycleRecordShape): InvariantLifecycleRecord {
  return shape as InvariantLifecycleRecord;
}

// ── Constructor + transitions ─────────────────────────────────

/** Create a fresh lifecycle record in the Proposed status. */
export function propose(invariant: string, description: string): InvariantLifecycleRecord {
  return brand({ invariant, description, status: "Proposed", history: [] });
}

/** Append a witness to a record and derive the resulting status. */
function appendWitness(
  record: InvariantLifecycleRecord,
  witness: LifecycleWitness,
  next: InvariantStatus,
): InvariantLifecycleRecord {
  return brand({
    invariant: record.invariant,
    description: record.description,
    status: next,
    history: [...record.history, witness],
  });
}

/** Error thrown when a transition is requested from an incompatible status. */
export class LifecycleTransitionError extends Error {
  constructor(
    public readonly from: InvariantStatus,
    public readonly attempted: LifecycleWitness["kind"],
  ) {
    super(
      `Lifecycle transition '${attempted}' is not permitted from status '${from}'. ` +
        "Status transitions are status-indexed by construction.",
    );
    this.name = "LifecycleTransitionError";
  }
}

/** Establish the invariant: Proposed → Established. */
export function establish(
  record: InvariantLifecycleRecord,
  witness: Omit<EstablishmentWitness, "kind">,
): InvariantLifecycleRecord {
  if (record.status !== "Proposed") {
    throw new LifecycleTransitionError(record.status, "establishment");
  }
  return appendWitness(record, { ...witness, kind: "establishment" }, "Established");
}

/** Record an invariant-preserving transition: Established | Preserved → Preserved. */
export function preserve(
  record: InvariantLifecycleRecord,
  witness: Omit<PreservationWitness, "kind">,
): InvariantLifecycleRecord {
  if (!isActiveStatus(record.status)) {
    throw new LifecycleTransitionError(record.status, "preservation");
  }
  return appendWitness(record, { ...witness, kind: "preservation" }, "Preserved");
}

/** Record an invariant violation: Established | Preserved → Violated. */
export function violate(
  record: InvariantLifecycleRecord,
  witness: Omit<ViolationWitness, "kind">,
): InvariantLifecycleRecord {
  if (!isActiveStatus(record.status)) {
    throw new LifecycleTransitionError(record.status, "violation");
  }
  return appendWitness(record, { ...witness, kind: "violation" }, "Violated");
}

/** Retire the invariant: Established | Preserved → Retired. */
export function retire(
  record: InvariantLifecycleRecord,
  witness: Omit<RetirementWitness, "kind">,
): InvariantLifecycleRecord {
  if (!isActiveStatus(record.status)) {
    throw new LifecycleTransitionError(record.status, "retirement");
  }
  return appendWitness(record, { ...witness, kind: "retirement" }, "Retired");
}

// ── Predicates over lifecycle history ─────────────────────────

/**
 * Permitted transition targets from a given status.
 *
 * Used by UIs to enable/disable buttons; matches the `data LifecyclePayload`
 * constructor coverage in the Agda module.
 */
export function permittedTransitions(status: InvariantStatus): readonly LifecycleWitness["kind"][] {
  switch (status) {
    case "Proposed":
      return ["establishment"];
    case "Established":
    case "Preserved":
      return ["preservation", "violation", "retirement"];
    case "Violated":
    case "Retired":
      return [];
  }
}

/** Total number of preservation witnesses in the record's history. */
export function preservationCount(record: InvariantLifecycleRecord): number {
  return record.history.filter((w) => w.kind === "preservation").length;
}

/** The latest establishment witness, if any. */
export function latestEstablishment(record: InvariantLifecycleRecord): EstablishmentWitness | null {
  for (let i = record.history.length - 1; i >= 0; i--) {
    const w = record.history[i];
    if (w?.kind === "establishment") return w;
  }
  return null;
}
